import { midnightMarketsByChain } from '@1delta/data-sdk'
import { ProxyOracleAbi } from '../../../abis/oracle/ProxyOracle'
import {
  FetcherResult,
  MulticallCall,
  OraclePriceEntry,
  ParseContext,
} from '../types'
import { formatMorphoPrice } from '../../../utils/parsing'
import { midnightCollateralRef } from '@1delta/data-sdk'
import { createMarketUid } from '../../../utils'
import { midnightLenderKey } from '../../../lending/public-data/midnight/convertPublic'

/**
 * Morpho Midnight oracle price fetcher.
 *
 * Midnight is a fixed-rate, fixed-maturity order-book protocol (NOT a Blue
 * fork), but each collateral leg of a market carries a Morpho-style oracle
 * that implements `price()` with identical scaling — verified on-chain: the
 * raw uint256 is `ratio * 10^(36 + loanDecimals - collateralDecimals)`, the
 * price of the collateral quoted in the loan asset. So the pricing math is
 * the same as `morpho.ts`; only the shape (one loan asset, N collateral legs
 * per market) and the lender key (`MORPHO_MIDNIGHT_<id>`) differ.
 *
 * Entries land under `midnightLenderKey(marketId)` so their marketUids join the
 * Midnight lending data emitted by `convertMidnightMarketsToResponse`.
 *
 * Like Morpho, Midnight prices are DERIVED: the collateral USD price needs the
 * loan asset's USD price from an earlier (Pass 1) parser. Parsed with
 * `updatePrices=false` so derived values don't overwrite direct oracle reads.
 */

/** One collateral leg of a Midnight market — one `price()` call. */
interface MidnightLeg {
  collateralAsset: string
  collateralDecimals: number
  oracle: string
  /**
   * The leg's ON-CHAIN `collateralIndex`, which is what the row uid is keyed by
   * (`midnightCollateralRef`). NOT the same as this leg's position in `legs`:
   * legs with no oracle are dropped below, so the array index is a call slot and
   * drifts from the protocol's index as soon as one leg is skipped.
   */
  collateralIndex: number
}

/** Grouped metadata: one Midnight market (loan asset + ordered collateral legs). */
interface MidnightMarketMeta {
  marketId: string
  loanAsset: string
  loanDecimals: number
  /** Ordered to match the emitted `price()` calls (index = call slot). */
  legs: MidnightLeg[]
}

/**
 * Build multicall calls for Midnight oracles.
 *
 * One `FetcherResult` per market; its `calls` are the per-leg `price()` reads
 * in collateral-leg order, so the parser can zip results back to legs by index.
 * Legs without a configured oracle are dropped (they can't be priced).
 */
export function getMidnightCalls(
  chainId: string,
): FetcherResult<MidnightMarketMeta>[] {
  const markets = midnightMarketsByChain(chainId)
  if (markets.length === 0) return []

  const results: FetcherResult<MidnightMarketMeta>[] = []

  for (const market of markets) {
    if (!market.marketId || !market.loanToken) continue

    const legs: MidnightLeg[] = []
    const calls: MulticallCall[] = []
    ;(market.collateralParams ?? []).forEach((c, collateralIndex) => {
      if (!c.oracle || !c.token) return
      legs.push({
        collateralAsset: c.token.toLowerCase(),
        collateralDecimals: c.decimals,
        oracle: c.oracle,
        collateralIndex,
      })
      calls.push({ address: c.oracle, name: 'price', params: [] })
    })

    if (legs.length === 0) continue

    results.push({
      calls,
      meta: {
        marketId: market.marketId,
        loanAsset: market.loanToken.toLowerCase(),
        loanDecimals: market.loanDecimals,
        legs,
      },
      lender: midnightLenderKey(market.marketId),
    })
  }

  return results
}

/**
 * Parse Midnight oracle results into structured price entries.
 *
 * Mirrors `parseMorphoResults`: each leg's oracle returns the collateral price
 * in loan-asset terms. USD conversion multiplies that ratio by the loan asset's
 * known USD price. Emits one loan-asset entry per market (price = 1, the
 * oracle's own unit of account) plus one entry per priced collateral leg.
 */
export function parseMidnightResults(
  data: any[],
  meta: MidnightMarketMeta,
  context: ParseContext,
): OraclePriceEntry[] {
  const { chainId, usdPrices, tokenList } = context
  const entries: OraclePriceEntry[] = []

  const loanAsset = meta.loanAsset
  const loanDecimals =
    meta.loanDecimals ?? tokenList?.[loanAsset]?.decimals ?? 18

  // The loan asset is the oracle's unit of account. Its USD comes from an
  // earlier parser (asset group first, then bare address).
  const loanOracleKey =
    tokenList?.[loanAsset]?.assetGroup ?? `${chainId}-${loanAsset}`
  const loanAssetUSD = usdPrices[loanOracleKey] ?? usdPrices[loanAsset]

  // Without the loan asset's USD price nothing on this market can be derived.
  if (!loanAssetUSD) return entries

  const lenderKey = midnightLenderKey(meta.marketId)

  // One loan-asset entry per market (price = 1 in the oracle's own denomination).
  entries.push({
    asset: loanAsset,
    price: 1,
    priceUSD: loanAssetUSD,
    marketUid: createMarketUid(chainId, lenderKey, loanAsset),
    targetLender: lenderKey,
    description: 'Midnight loan asset',
    staticBase: true,
    baseAsset: loanAsset,
  })

  // One entry per collateral leg, zipped to calls by index.
  meta.legs.forEach((leg, i) => {
    const rawPrice = data[i]
    if (!rawPrice || rawPrice === '0x') return

    const collateralDecimals =
      leg.collateralDecimals ?? tokenList?.[leg.collateralAsset]?.decimals ?? 18

    const priceDebtToCollateral = formatMorphoPrice(
      rawPrice.toString(),
      loanDecimals,
      collateralDecimals,
    )

    if (isNaN(priceDebtToCollateral) || priceDebtToCollateral === 0) return

    entries.push({
      asset: leg.collateralAsset,
      price: priceDebtToCollateral, // raw: collateral price in loan asset terms
      priceUSD: priceDebtToCollateral * loanAssetUSD,
      marketUid: createMarketUid(
        chainId,
        lenderKey,
        midnightCollateralRef(leg.collateralAsset, leg.collateralIndex),
      ),
      targetLender: lenderKey,
      baseAsset: loanAsset,
    })
  })

  return entries
}

/** ABI for Midnight oracle calls (same `price()` interface as Morpho). */
export function getMidnightAbi() {
  return ProxyOracleAbi
}

/**
 * Morpho Midnight Oracle Fetcher module
 */
export const midnightFetcher = {
  getCalls: getMidnightCalls,
  parse: parseMidnightResults,
  getAbi: getMidnightAbi,
}
